import { useState, useMemo, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { updateGameInfo } from "@/lib/stores/unifiedUserStore";
import { getLanguageName, getLanguageFlag } from "@/lib/dataSources";
import { MultiSelectDropdownWithField } from "@/components/ui/dropdowns/MultiSelectDropdownWithField";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { syncTranslatableStrings } from "@/lib/utils/syncTranslatableStrings";

const AVAILABLE_LANGUAGES = [
  'en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'ar', 'hi',
  'nl', 'sv', 'no', 'da', 'fi', 'pl', 'tr', 'he', 'th', 'vi', 'id', 'ms',
  'tl', 'el', 'hu', 'cs', 'sk', 'ro', 'bg', 'hr', 'sl', 'et', 'lv', 'lt',
  'mt', 'ga', 'cy', 'is', 'fo', 'sq', 'mk', 'sr', 'bs', 'me', 'uk', 'be',
  'kk', 'ky', 'uz', 'tg', 'mn', 'ka', 'hy', 'az', 'fa', 'ur', 'bn', 'ta',
  'te', 'kn', 'ml', 'gu', 'pa', 'or', 'as', 'ne', 'si', 'my', 'km', 'lo',
  'am', 'sw', 'zu', 'af', 'xh', 'yo', 'ig', 'ha', 'so', 'rw', 'lg', 'ak',
  'tw', 'ee',
];

export default function LanguageSetupPrompt() {
  const { toast } = useToast();
  const { selectedGame, refreshData } = useSimplifiedUserData();

  const [languages, setLanguages] = useState<string[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const gameLanguages = selectedGame?.settings?.languages;
  const hasNoLanguages =
    !gameLanguages || (Array.isArray(gameLanguages) && gameLanguages.length === 0);

  const languageOptions = useMemo(
    () =>
      AVAILABLE_LANGUAGES.map((code) => ({
        id: code,
        label: `${getLanguageFlag(code)} ${getLanguageName(code)}`,
        description: code,
      })),
    []
  );

  const handleLanguageChange = useCallback(
    (selected: string | string[]) => {
      const selectedArray = Array.isArray(selected) ? selected : [selected];
      setLanguages(selectedArray);

      if (selectedArray.length === 1) {
        setDefaultLanguage(selectedArray[0]);
      } else if (!selectedArray.includes(defaultLanguage)) {
        setDefaultLanguage("");
      }
    },
    [defaultLanguage]
  );

  const handleSave = async () => {
    if (!selectedGame?.id) return;

    if (languages.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one language.",
        variant: "destructive",
      });
      return;
    }

    const resolvedDefault = languages.length === 1 ? languages[0] : defaultLanguage;

    if (!resolvedDefault) {
      toast({
        title: "Error",
        description: "Please select a default language.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateGameInfo(
        selectedGame.id,
        {
          settings: {
            ...selectedGame.settings,
            languages,
            defaultLanguage: resolvedDefault,
          },
        },
        toast
      );

      await refreshData();

      toast({
        title: "Languages configured",
        description: "Syncing translatable strings...",
      });

      await syncTranslatableStrings(selectedGame.id, toast);

      toast({
        title: "Setup complete",
        description: "Languages and translatable strings are configured.",
      });
    } catch (error) {
      console.error("[LanguageSetupPrompt] Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasNoLanguages || !selectedGame) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[520px] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="items-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-2">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle>Set up languages for your game</DialogTitle>
          <DialogDescription className="text-center">
            Your game <strong>{selectedGame.name}</strong> needs at least one
            language configured before you can create translatable content.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Supported Languages *
            </label>
            <MultiSelectDropdownWithField
              placeholder="Select languages..."
              options={languageOptions}
              value={languages}
              onChange={handleLanguageChange}
              multiple={true}
              showSelectedOptions={true}
              disabled={isSaving}
            />
            <p className="mt-1 text-sm text-muted-foreground">
              Select all languages that will be supported in this game.
            </p>
          </div>

          {languages.length > 1 && (
            <div>
              <label
                htmlFor="setup-default-language"
                className="block text-sm font-medium text-foreground mb-2"
              >
                Default Language *
              </label>
              <select
                id="setup-default-language"
                value={defaultLanguage}
                onChange={(e) => setDefaultLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded bg-background focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isSaving}
              >
                <option value="">Select default language...</option>
                {languageOptions
                  .filter((lang) => languages.includes(lang.id))
                  .map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.label}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-sm text-muted-foreground">
                The primary language for this game's source text.
              </p>
            </div>
          )}

          {languages.length === 1 && (
            <p className="text-sm text-muted-foreground">
              <strong>{getLanguageFlag(languages[0])} {getLanguageName(languages[0])}</strong>{" "}
              will be set as the default language.
            </p>
          )}
        </div>

        <DialogFooter className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={
              isSaving ||
              languages.length === 0 ||
              (languages.length > 1 && !defaultLanguage)
            }
          >
            {isSaving ? "Setting up..." : "Save & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
